import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Info,
} from 'lucide-react';
import {
  AYUDA_REPORTES,
  type ClaveAyuda,
  type Frase,
  type Lectura,
  type NivelFrase,
} from '@suminuta/shared';

/**
 * Bloque "Lectura" y panel de ayuda (PROMPT_N°056).
 *
 * Los reportes mostraban números correctos pero mudos. Esto los hace decir
 * qué están mostrando y qué merece atención, con frases que vienen del
 * backend —reglas fijas sobre los datos ya calculados, no texto generado— y
 * que por eso pueden enlazar a las filas exactas de las que hablan.
 *
 * Una frase con `filtro` es clickeable: aplica ese filtro al reporte y baja
 * a la tabla. Es lo que convierte "3 productos no cuadran" en algo
 * accionable en vez de en un dato que hay que ir a buscar a mano.
 */

const ICONO: Record<NivelFrase, typeof Info> = {
  alerta: AlertTriangle,
  atencion: AlertTriangle,
  ok: CheckCircle2,
  info: Info,
};

const TONO: Record<NivelFrase, { texto: string; icono: string }> = {
  alerta: {
    texto: 'text-red-800 dark:text-red-200',
    icono: 'text-red-600 dark:text-red-400',
  },
  atencion: {
    texto: 'text-amber-900 dark:text-amber-200',
    icono: 'text-amber-600 dark:text-amber-400',
  },
  ok: {
    texto: 'text-emerald-900 dark:text-emerald-200',
    icono: 'text-emerald-600 dark:text-emerald-400',
  },
  info: {
    texto: 'text-gray-700 dark:text-gray-300',
    icono: 'text-gray-400 dark:text-gray-500',
  },
};

const FONDO: Record<NivelFrase, string> = {
  alerta: 'bg-red-50 dark:bg-red-900/20',
  atencion: 'bg-amber-50 dark:bg-amber-900/20',
  ok: '',
  info: '',
};

interface Props {
  lectura?: Lectura;
  /** Clave del texto de ayuda; si falta, no se muestra el panel. */
  ayuda?: ClaveAyuda;
  /** Aplica el filtro de una frase clickeable. */
  onFiltro?: (filtro: Record<string, string | number | boolean>) => void;
  cargando?: boolean;
}

export default function BloqueLectura({
  lectura,
  ayuda,
  onFiltro,
  cargando,
}: Props) {
  const [abierto, setAbierto] = useState(true);
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  if (cargando || !lectura || lectura.frases.length === 0) return null;

  const peor = lectura.frases[0]?.nivel ?? 'info';
  const textoAyuda = ayuda ? AYUDA_REPORTES[ayuda] : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setAbierto(!abierto)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
        aria-expanded={abierto}
      >
        <span className="flex items-center gap-2">
          {abierto ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Lectura
          </span>
          {!abierto && (
            <span className={`text-xs ${TONO[peor].texto} truncate max-w-xl`}>
              {lectura.frases[0]?.texto}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-400">
          {lectura.frases.length}{' '}
          {lectura.frases.length === 1 ? 'observación' : 'observaciones'}
        </span>
      </button>

      {abierto && (
        <ul className="px-4 pb-3 space-y-1.5">
          {lectura.frases.map((f, i) => (
            <FilaFrase key={i} frase={f} onFiltro={onFiltro} />
          ))}
        </ul>
      )}

      {textoAyuda && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setAyudaAbierta(!ayudaAbierta)}
            className="w-full flex items-center gap-2 px-4 py-2 text-left text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            aria-expanded={ayudaAbierta}
          >
            <HelpCircle className="h-3.5 w-3.5" />
            ¿Qué es este reporte?
            {ayudaAbierta ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {ayudaAbierta && (
            <div className="px-4 pb-4 pt-1 space-y-2 text-sm text-gray-700 dark:text-gray-300 max-w-4xl">
              <p>
                <strong className="text-gray-900 dark:text-gray-100">
                  Para qué sirve:
                </strong>{' '}
                {textoAyuda.paraQue}
              </p>
              <p>
                <strong className="text-gray-900 dark:text-gray-100">
                  {textoAyuda.como.etiqueta}:
                </strong>{' '}
                {textoAyuda.como.texto}
              </p>
              <p>
                <strong className="text-gray-900 dark:text-gray-100">
                  Qué mirar:
                </strong>{' '}
                {textoAyuda.queMirar}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilaFrase({
  frase,
  onFiltro,
}: {
  frase: Frase;
  onFiltro?: Props['onFiltro'];
}) {
  const Icono = ICONO[frase.nivel];
  const tono = TONO[frase.nivel];
  const clickeable = Boolean(frase.filtro && onFiltro);

  const contenido = (
    <>
      <Icono className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tono.icono}`} />
      <span className={`text-sm ${tono.texto}`}>
        {frase.texto}
        {clickeable && (
          <span className="ml-1 text-xs text-blue-600 dark:text-blue-400 underline">
            ver
          </span>
        )}
      </span>
    </>
  );

  const fondo = FONDO[frase.nivel];

  if (!clickeable) {
    return (
      <li className={`flex items-start gap-2 rounded px-2 py-1 ${fondo}`}>
        {contenido}
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={() => onFiltro!(frase.filtro!)}
        className={`w-full flex items-start gap-2 text-left rounded px-2 py-1 ${fondo} hover:bg-gray-100 dark:hover:bg-gray-700/60`}
      >
        {contenido}
      </button>
    </li>
  );
}
